import { _decorator, Component, PageView, CCInteger } from 'cc';
import { DEV } from 'cc/env';

declare module 'cc' {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface PageView {
        pageOffset: number;
        getCurrentPage(): Node;
        getActualPageIndex(): number;
        scrollToActualPage(pageIdx: number, timeInSecond?: number): void;
    }
}

PageView.prototype.getActualPageIndex = function getActualPageIndex(this: PageView) {
    return (this.getCurrentPageIndex() + (this.pageOffset ?? 0)) % this.getPages().length;
};
PageView.prototype.getCurrentPage = function getCurrentPage(this: PageView) {
    return this._pages[this.getActualPageIndex()];
};

enum EventType {
    STARTED = 'STARTED',
}

const { ccclass, property, requireComponent } = _decorator;

@ccclass('VirtualPageView')
@requireComponent(PageView)
export default class VirtualPageView extends Component {
    public static readonly EVENT_TYPE = EventType;

    @property
    private _defaultPageIndex: number = 0;

    private _pageView: PageView = null;

    @property({ type: CCInteger, min: 0, step: 1, tooltip: DEV && '預設顯示頁面索引' })
    public get defaultPageIndex() {
        return this._defaultPageIndex;
    }
    public set defaultPageIndex(value: number) {
        if (this._defaultPageIndex === value) {
            return;
        }
        this._defaultPageIndex = value;
    }

    private get pageView() {
        if (!this._pageView) {
            this._pageView = this.getComponent(PageView);
        }
        return this._pageView;
    }

    protected onLoad() {
        this.pageView.node.on(PageView.EventType.SCROLL_ENDED, this._onScrollEnded, this);
        const self = this;
        const originalScrollToPage = this.pageView.scrollToPage;
        this.pageView.scrollToPage = function scrollToPage(this: PageView, pageIdx: number, timeInSecond?: number) {
            const indexChanged = this.curPageIdx !== pageIdx;
            originalScrollToPage.call(this, pageIdx, timeInSecond);
            // scroll ended event won't be triggered if timeInSecond is 0, so we need to trigger it manually
            if (indexChanged && timeInSecond === 0) {
                self._onScrollEnded();
            }
        };
        this.pageView.scrollToActualPage = function scrollToActualPage(this: PageView, pageIdx: number, timeInSecond?: number) {
            const actualPageIdx = (pageIdx - (this.pageOffset ?? 0) + this.getPages().length) % this.getPages().length;
            this.scrollToPage(actualPageIdx, timeInSecond);
        };
    }

    protected start() {
        const pages = this.pageView.getPages();
        const pageCount = pages.length;
        if (pageCount <= 1) {
            return;
        }
        this.pageView.pageOffset = (pageCount - 1 + this.defaultPageIndex) % pageCount;
        this.pageView.scrollToPage(1, 0);
        // force trigger page turning event, let scrollToPage immediately
        // @ts-expect-error
        this.pageView._dispatchPageTurningEvent();
        this.emit(VirtualPageView.EVENT_TYPE.STARTED);
    }

    private _onScrollEnded() {
        const pages = this.pageView.getPages();
        if (pages.length <= 1) {
            return;
        }
        const pageCount = pages.length;
        // record page index change offset
        this.pageView.pageOffset += this.pageView.curPageIdx - 1;
        // ensure page offset is in range
        this.pageView.pageOffset = (this.pageView.pageOffset + pageCount) % pageCount;

        // reorder page zIndex according to page index and page offset
        pages.forEach((page, index) => {
            page._zIndex = (index - this.pageView.pageOffset + pageCount) % pageCount;
        });
        this.pageView.content.reorderChildren();

        // reset current page index
        this.pageView.scrollToPage(1, 0);
        // @ts-ignore
        this.pageView._lastPageIdx = 1;
    }
}
